/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { INITIAL_SETTINGS } from '@/data/config/DEFAULTS';
import { SITE_STORE_ID } from '@/data/config/SITE_STORE_ID';
import { STORE_IDENTIFIER } from '@/data/config/STORE_IDENTIFIER';
import { CONFIGURATION_QUERY } from '@/data/constants/configuration';
import { SAS_STORE_REL_TYPE } from '@/data/constants/environment';
import { STORE_TOKEN } from '@/data/constants/seo';
import {
	configurationByQueryFetcher,
	dataMap as configurationDataMap,
} from '@/data/Content/_ConfigurationFetcher';
import { CONFIGURATION_IDS, LanguageConfiguration } from '@/data/types/Configuration';
import { Token } from '@/data/types/Token';
import { UserContext } from '@/data/types/UserContext';
import { getRequestId } from '@/data/utils/getRequestId';
import { errorWithId } from '@/data/utils/loggerUtil';
import { Configuration } from 'integration/generated/transactions/Configuration';
import type {
	StoreStoreIdentifierItem,
	StoreStoreItem,
	TokenToken,
} from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import transactionsStore from 'integration/generated/transactions/transactionsStore';
import transactionsToken from 'integration/generated/transactions/transactionsToken';
import { GetServerSidePropsContext } from 'next';
import {
	DIBOT_APP_OMNICHANNEL_ORG_ID_KEY,
	DIBOT_APP_OMNICHANNEL_ORG_URL_KEY,
	DIBOT_APP_OMNICHANNEL_WIDGET_ID_KEY,
	DIBOT_OMNICHANNEL_ORG_ID_KEY,
	DIBOT_OMNICHANNEL_ORG_URL_KEY,
	DIBOT_OMNICHANNEL_WIDGET_ID_KEY,
	DIBOT_RECAPTCHA_SITE_KEY_KEY,
	ENABLE_DIBOT_REACT_FLAG,
	ENABLE_DIBOT_RECAPTCHA_FLAG,
} from 'shared/custom/constants/commonConstants';

/**
 * Surfaces the DiBot widget's server-only env vars through settings.userData so
 * the client widget can read them without a NEXT_PUBLIC_ prefix:
 *  - ENABLE_DIBOT=true              → userData.ENABLE_DIBOT_REACT='true'
 *  - DIBOT_OMNICHANNEL_ORG_URL/ID/WIDGET_ID → matching userData keys (web widget)
 *  - DIBOT_APP_OMNICHANNEL_ORG_URL/ID/WIDGET_ID → matching userData keys
 *    (mobile app webview widget — separate Copilot Studio project)
 *  - DIBOT_OMNICHANNEL_ORG_URL/ID/WIDGET_ID → matching userData keys
 *  - DIBOT_RECAPTCHA_SITE_KEY        → userData.DIBOT_RECAPTCHA_SITE_KEY
 *    (a Score-based reCAPTCHA Enterprise key used for a silent, invisible check)
 *  - ENABLE_DIBOT_RECAPTCHA=true     → userData.ENABLE_DIBOT_RECAPTCHA='true'
 *    (master on/off switch for the check, independent of the site key)
 * Values are only injected when the env var is present, so HCL storeconf still
 * wins in environments where these env vars are not set. Only the reCAPTCHA
 * site key (a public value) is surfaced here — the API key, project id, and
 * score threshold stay server-only, read directly from process.env inside the
 * verify API route.
 */
const applyDibotUserData = (settings: Settings): void => {
	const overrides: Record<string, string> = {};
	if (process.env.ENABLE_DIBOT === 'true') overrides[ENABLE_DIBOT_REACT_FLAG] = 'true';
	if (process.env.ENABLE_DIBOT_RECAPTCHA === 'true')
		overrides[ENABLE_DIBOT_RECAPTCHA_FLAG] = 'true';
	if (process.env.DIBOT_OMNICHANNEL_ORG_URL)
		overrides[DIBOT_OMNICHANNEL_ORG_URL_KEY] = process.env.DIBOT_OMNICHANNEL_ORG_URL;
	if (process.env.DIBOT_OMNICHANNEL_ORG_ID)
		overrides[DIBOT_OMNICHANNEL_ORG_ID_KEY] = process.env.DIBOT_OMNICHANNEL_ORG_ID;
	if (process.env.DIBOT_OMNICHANNEL_WIDGET_ID)
		overrides[DIBOT_OMNICHANNEL_WIDGET_ID_KEY] = process.env.DIBOT_OMNICHANNEL_WIDGET_ID;
	if (process.env.DIBOT_APP_OMNICHANNEL_ORG_URL)
		overrides[DIBOT_APP_OMNICHANNEL_ORG_URL_KEY] = process.env.DIBOT_APP_OMNICHANNEL_ORG_URL;
	if (process.env.DIBOT_APP_OMNICHANNEL_ORG_ID)
		overrides[DIBOT_APP_OMNICHANNEL_ORG_ID_KEY] = process.env.DIBOT_APP_OMNICHANNEL_ORG_ID;
	if (process.env.DIBOT_APP_OMNICHANNEL_WIDGET_ID)
		overrides[DIBOT_APP_OMNICHANNEL_WIDGET_ID_KEY] = process.env.DIBOT_APP_OMNICHANNEL_WIDGET_ID;
	if (process.env.DIBOT_RECAPTCHA_SITE_KEY)
		overrides[DIBOT_RECAPTCHA_SITE_KEY_KEY] = process.env.DIBOT_RECAPTCHA_SITE_KEY;
	if (Object.keys(overrides).length > 0) {
		settings.userData = { ...(settings.userData ?? {}), ...overrides };
	}
};

// TODO Settings logic needs to be reviewed and extended.
export type Settings = {
	supportedCurrencies: string[];
	defaultCurrency: string;
	supportedLanguages: string[];
	defaultLanguage: string;
	currencySymbol: string;
	storeId: string;
	inventorySystem: number;
	defaultCatalogId: string;
	storeName: string;
	identifier: string;
	userData: Record<string, string>;
	csrSession: boolean;
	mapApiKey?: string;
	inPreview?: boolean;
	/**
	 * StoreToken resolved from URL.
	 */
	storeToken?: Token;
	context?: UserContext;
	storeType?: string;
	[CONFIGURATION_IDS.SUPPORTED_LANGUAGES]?: LanguageConfiguration[];
	[CONFIGURATION_IDS.DEFAULT_LANGUAGE]?: LanguageConfiguration[];
	[extra: string]: any; // more specific later on based usage.
};

const currencySymbols: Record<string, string> = {
	MXN: '$',
};

const dataMap = ({
	supportedCurrencies,
	supportedLanguages,
	storeId,
	description,
	identifier,
	...rest
}: StoreStoreItem): Omit<Omit<Settings, 'inventorySystem'>, 'defaultCatalogId'> => ({
	supportedCurrencies: supportedCurrencies?.supportedCurrencies || [],
	defaultCurrency: supportedCurrencies?.defaultCurrency || '',
	supportedLanguages: supportedLanguages?.supportedLanguages || [],
	defaultLanguage: supportedLanguages?.defaultLanguageId || '',
	currencySymbol: currencySymbols[supportedCurrencies?.defaultCurrency || ''],
	storeId: storeId ?? '',
	storeName: (description?.at(0)?.displayName ?? identifier) as string,
	identifier,
	...rest,
});

const extractStoreItemFirstResult = (data: { resultList?: StoreStoreItem[] }) =>
	data?.resultList?.at(0) || {};

const extractStoreTokenFirstResult = (data: TokenToken): Token | undefined =>
	data?.resultList?.filter((token) => token.tokenName === STORE_TOKEN).at(0);

const extractStoreStoreIdentifierItemFirstResult = (data: {
	resultList?: StoreStoreIdentifierItem[];
}) => data?.resultList?.at(0) || {};

const fetchAdminData = async (pub: boolean, storeIdentifier: string, params?: RequestParams) =>
	extractStoreStoreIdentifierItemFirstResult(
		await transactionsStore(pub).storeFindByStoreNameAndUsage(
			SITE_STORE_ID,
			{
				q: 'findByStoreIdentifier',
				storeIdentifier,
			},
			params
		)
	);

const fetchOnlineStoreData = async (pub: boolean, storeId: string, params?: RequestParams) =>
	dataMap(
		extractStoreItemFirstResult(
			await transactionsStore(pub).storeFindByQueryOnlineStore(
				storeId,
				{
					q: 'findOnlineStore',
				},
				params
			)
		)
	);

const fetchStoreIdFromToken = async (
	pub: boolean,
	storeTokenCandidate: string,
	params?: RequestParams
) =>
	extractStoreTokenFirstResult(
		await transactionsToken(pub).tokenFindByUrlKeywordNames(
			SITE_STORE_ID,
			{
				q: 'byUrlKeywordNames',
				urlKeywordName: [storeTokenCandidate],
			},
			params
		)
	);
const fetchLanguageConfig = async (storeId: string, context?: GetServerSidePropsContext) => {
	const props = {
		storeId,
		query: {
			q: CONFIGURATION_QUERY.BY_CONFIGURATION_IDS as Parameters<
				Configuration['configurationFindByQuery']
			>['1']['q'],
			configurationId: [CONFIGURATION_IDS.SUPPORTED_LANGUAGES, CONFIGURATION_IDS.DEFAULT_LANGUAGE],
		},
	};
	return configurationDataMap(await configurationByQueryFetcher(false, context)(props));
};

export const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async ({
		storeIdentifier,
		storeId: iStoreId,
		shopAsUser,
		storeTokenCandidate,
	}: {
		storeIdentifier?: string;
		storeId?: string;
		shopAsUser?: string;
		storeTokenCandidate?: string;
	}): Promise<Settings> => {
		try {
			const params = { _requestId: getRequestId(context) } as RequestParams;
			const result = {
				csrSession: !!shopAsUser,
				mapApiKey: process.env.MAP_API_KEY ?? '',
			} as Settings;
			let storeId = iStoreId;

			const storeToken = storeTokenCandidate
				? await fetchStoreIdFromToken(pub, storeTokenCandidate, params)
				: undefined;
			/**
			 * {
			 *   "tokenName": "StoreToken:CatalogToken",
			 *   "urlKeywordName": "ruby",
			 *   "tokenValue": "41:",
			 *   "urlKeywordId": 62501,
			 *   "status": 1
			 * }
			 */
			const tokenStoreId = storeToken?.tokenValue?.split(':').at(0);
			if (!storeId && !storeIdentifier && tokenStoreId) {
				storeId = tokenStoreId;
			}
			if (storeId) {
				Object.assign(result, await fetchOnlineStoreData(pub, storeId, params));
				Object.assign(result, await fetchAdminData(pub, result.identifier, params));
			} else {
				Object.assign(
					result,
					await fetchAdminData(pub, storeIdentifier ?? STORE_IDENTIFIER, params)
				);
				Object.assign(result, await fetchOnlineStoreData(pub, result.storeId, params));
			}
			Object.assign(result, await fetchLanguageConfig(result.storeId, context));
			if (result.storeId === tokenStoreId) {
				Object.assign(result, { storeToken });
			}
			result.relatedStores = result.relatedStores?.filter(
				(rel: any) => rel.relationshipType === SAS_STORE_REL_TYPE
			);
			applyDibotUserData(result);
			return result;
		} catch (error) {
			errorWithId(getRequestId(context), '_Settings: fetcher: error', { error });
			return {
				error: true,
				...INITIAL_SETTINGS,
				csrSession: !!shopAsUser,
			};
		}
	};
